import { HighnoteTransactionEvent, Paidol } from 'API';

// Default minimum amount in cents (7500 cents = $75.00)
const DEFAULT_MINIMUM_AMOUNT_CENTS = 7500;

/**
 * Determines if a receipt is required for a transaction based on receipt settings.
 *
 * A receipt IS required if:
 * Receipt settings are enabled and transaction amount is at or above the minimum threshold and card is not exempt
 *
 * @param transaction - The transaction to check
 * @param paidol - The paidol with receipt settings
 * @returns true if receipt is required, false otherwise
 */
export function isReceiptRequired(transaction: HighnoteTransactionEvent, paidol?: Paidol | null): boolean {
  const receiptsSettings = paidol?.receiptsSettings;

  // If receipt settings are disabled, receipts are not required (minimum amount is ignored)
  if (!receiptsSettings?.isEnabled) {
    return false;
  }

  const minimumAmountCents = receiptsSettings?.minimumAmount ?? DEFAULT_MINIMUM_AMOUNT_CENTS;
  const exemptCardIds = receiptsSettings?.exemptCardIds ?? [];

  // If transaction has no payment card, require receipt (edge case)
  if (!transaction.paymentCardId) {
    return true;
  }

  // Check if card is exempt
  if (exemptCardIds.includes(transaction.paymentCardId)) {
    return false;
  }

  // Get transaction amount in cents (already in cents, no conversion needed)
  const transactionAmountCents = transaction.approvedAmount?.value ?? transaction.approvedAmountValue ?? null;
  // If we can't determine amount, require receipt (conservative default)
  if (transactionAmountCents === null) {
    return true;
  }
  // Compare directly in cents - if amount is below minimum, receipt is not required
  if (Math.abs(transactionAmountCents) < minimumAmountCents) {
    return false;
  }

  // Otherwise, receipt is required
  return true;
}
